import { useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Item } from '../types';
import { PRINT_MODE_STYLES } from '../constants/print';
import { useAutoScaleFont } from '../hooks/useAutoScaleFont';
import './ItemCardExpanded.css';

interface ItemCardExpandedProps {
  item: Item;
  isPrintMode?: boolean;
  borderColor?: string;
}

export function ItemCardExpanded({ item, isPrintMode, borderColor }: ItemCardExpandedProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  
  const fontSize = useAutoScaleFont({
    cardRef,
    descriptionRef,
    entityId: item.id,
    entityType: 'item',
    deps: [item.description, item.name, isPrintMode],
  });

  // Build dynamic styles for border color
  const dynamicStyles: React.CSSProperties = {};
  if (borderColor) {
    dynamicStyles.border = `4px solid ${borderColor}`;
  }

  // Compute print mode styles with border override when in print mode
  const printStyles: React.CSSProperties | undefined = isPrintMode
    ? { ...PRINT_MODE_STYLES, ...(borderColor ? { border: `4px solid ${borderColor}` } : {}) }
    : undefined;

  return (
    <div 
      ref={cardRef}
      className={`item-card-expanded ${isPrintMode ? 'print-mode' : ''}`}
      style={printStyles ?? (borderColor ? dynamicStyles : undefined)}
    >
      <div className="card-header">
        <h2>
          {item.name}
          {item.attunement && (
            <span className="attunement-icon" title="Requires Attunement">
              <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true">
                <path d="M12 2 L22 12 L12 22 L2 12 Z" fill="none" stroke="currentColor" strokeWidth="2"/>
                <text x="12" y="16" textAnchor="middle" fontSize="12" fontWeight="bold" fill="currentColor">A</text>
              </svg>
            </span>
          )}
        </h2>
      </div>

      <div className="card-content">
        {/* Stats row: damage/AC, range, mastery */}
        {(item.damage || item.ac || item.range || item.weaponMastery) && (
          <div className="card-stats">
            {item.damage && (
              <span className="stat damage" title="Damage">
                <span className="stat-icon">⚔</span> {item.damage}
              </span>
            )}
            {item.ac && (
              <span className="stat ac" title="Armor Class">
                <span className="stat-icon">⛨</span> {item.ac}
              </span>
            )}
            {item.range && (
              <span className="stat range" title="Range">
                <span className="stat-icon">◎</span> {item.range}
              </span>
            )}
            {item.weaponMastery && (
              <span className="stat mastery" title="Weapon Mastery">
                <span className="stat-icon">✦</span> {item.weaponMastery}
              </span>
            )}
          </div>
        )}

        <div 
          ref={descriptionRef}
          className="card-description"
          style={{ '--scale-factor': fontSize / 100 } as React.CSSProperties}
        >
          <Markdown remarkPlugins={[remarkGfm]}>{item.description}</Markdown>
        </div>
      </div>

      {item.charges && (
        <div className="card-charges">
          <span className="charges-label">Charges</span>
          <span className="charges-boxes">
            {Array.from({ length: item.charges }, (_, i) => (
              <svg key={i} viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5" rx="1"/>
              </svg>
            ))}
          </span>
        </div>
      )}

      <div className="card-footer">
        <span className="footer-left">
          <span className="rarity">{item.rarity}</span>
          <span className="footer-separator"> - </span>
          <span className="item-type">{item.type}</span>
        </span>
        {item.weight && <span className="weight">{item.weight}</span>}
      </div>
    </div>
  );
}
